import {
	ErrorModal,
	useContextMenuMESF,
	useMesfRealtime,
} from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	ColDef,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	IStatusPanelParams,
	MenuItemDef,
	RowClickedEvent,
	RowSelectedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { Group, Panel } from "react-resizable-panels";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { CustomStatusPanel } from "@/controls/ag-grid/components/CustomStatusPanel";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { UniversalRoll } from "@/pages/catalogs/models/UniversalRoll";
import { deleteUniversalRoll } from "@/pages/catalogs/repositories/UniversalRollRepository";
import { ActiveCarrierModal } from "@/pages/catalogs/shared/components/ActiveCarrierModal";
import { CustomSeparator } from "@/pages/catalogs/shared/components/CustomSeparator";
import { MultipleDeleteSelectModal } from "@/pages/catalogs/shared/components/MultipleDeleteSelectModal";
import { RollsetDeactivationConfirmModal } from "@/pages/catalogs/shared/components/RollsetDeactivationConfirmModal";
import { useResizablePanels } from "@/pages/catalogs/shared/hooks/useResizablePanels";
import { themeDXT } from "@/setup/ag-grid-license";
import { routeUniversalRoll } from "@/setup/routes/catalog.routes";
import { DeleteEntity } from "@/shared/components/DeleteEntity";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { masterDetailsMinHeight, pageBgColor } from "@/utils/theme";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CatalogsActionsHeader } from "../../../shared/components/CatalogsActionsHeader";
import { useTableData } from "../hooks/useTableData";
import { useUniversalRollOptionsFunctions } from "../hooks/useUniversalRollOptionsFunctions";
import { useUniversalRolls } from "../UniversalRollPage";
import { UniversalRollDetails } from "./UniversalRollDetails";

export const TableUniversalRoll = ({
	partId,
}: {
	partId: string | undefined;
}) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [filterValue, setFilterValue] = useState("");
	const [selectedRowData, setSelectedRowData] = useState<UniversalRoll | null>(
		null,
	);
	const [selectedRows, setSelectedRows] = useState<UniversalRoll[]>([]);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		| "create"
		| "update"
		| "delete"
		| "deleteMultiple"
		| "confirmRollsetDeactivation"
		| "activeCarrier"
		| ""
	>("");
	const [error, setError] = useState("");

	const {
		groupRef,
		getSavedPanelLayout,
		handlePanelResize,
		handleSeparatorDoubleClick,
	} = useResizablePanels("universal-roll-table-panel-sizes");

	const { data: rows, isLoading } = useUniversalRolls(undefined, (error) =>
		setError(error.message),
	);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useUniversalRollOptionsFunctions({
		setSelectedRowData,
		setShowModal,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowSelected = (event: RowSelectedEvent) => {
		const selectedNodes = gridApi?.getSelectedNodes() || [];
		const selectedData = selectedNodes.map(
			(node) => node.data as UniversalRoll,
		);
		setSelectedRows(selectedData);
	};

	const onRowClicked = (event: RowClickedEvent) => {
		const cell = gridApi?.getFocusedCell();
		if (cell?.column.getColId() !== "-" && event.data) {
			setSelectedRowData(event.data);
			setShowModal("update");
			navigate(`${routeUniversalRoll}/${(event.data as UniversalRoll).PartId}`);
		}
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			const selectedRows = params.api?.getSelectedRows();
			showContextMenu(
				event as any,
				{ ...data, selectedRows: selectedRows },
				"universalRollContext",
			);
			return [];
		},
		[rows],
	);

	const onModalHide = ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => {
		if (close) {
			setShowModal("");
			setSelectedRowData(null);
			if (gridApi) gridApi?.deselectAll();
			return;
		}
		if (entityId && shouldUpdate) {
			setTimeout(() => {
				navigate(`${routeUniversalRoll}/${entityId}`);
				const rowNode = gridApi?.getRowNode(String(entityId));

				if (rowNode!) {
					gridApi?.ensureNodeVisible(rowNode);
					rowNode.setSelected(true);
					setSelectedRowData(rowNode.data);
				}
			}, 0);
		}
	};

	const handleClearSelection = () => {
		gridApi?.deselectAll();
		setSelectedRows([]);
	};

	const handleNewRoll = () => {
		if (gridApi) gridApi.deselectAll();
		setShowModal("create");
		setSelectedRowData(null);
		navigate(`${routeUniversalRoll}/new`);
	};

	const handleExportToExcel = () => {
		if (gridApi) {
			const now = new Date();
			const year = now.getFullYear();
			const month = String(now.getMonth() + 1).padStart(2, "0");
			const day = String(now.getDate()).padStart(2, "0");
			const formattedDate = `${year}-${month}-${day}`;

			const columnKeys = gridApi
				.getColumnDefs()
				?.filter(
					(col): col is ColDef =>
						"field" in col && typeof col.field === "string",
				)
				.filter((col) => col.field !== "-")
				.map((col) => col.field)
				.filter((field): field is string => typeof field === "string");

			const params = {
				skipHeader: false,
				skipFooters: true,
				skipGroups: true,
				fileName: `universal_rolls_${formattedDate}.xlsx`,
				columnKeys: columnKeys,
				sheetName: "Universal Rolls",
				allColumns: false,
			};

			gridApi.exportDataAsExcel(params);
		} else {
			setError("Unable to export data. Please try again.");
		}
	};

	useMesfRealtime({
		onReceiveMessage: async (author, message) => {
			if (
				message.replaceAll("[", "").replaceAll("]", "") ==
				"M2.GetUniversalRolls"
			) {
				await queryClient.invalidateQueries({ queryKey: ["universalRolls"] });
			}
		},
	});

	useEffect(() => {
		if (!selectedRowData || !rows) return;
		const refreshedUniversalRoll = rows.find(
			(d) => d.PartId === selectedRowData.PartId,
		);
		if (refreshedUniversalRoll && refreshedUniversalRoll !== selectedRowData) {
			setSelectedRowData(refreshedUniversalRoll);
		}
	}, [rows]);

	useEffect(() => {
		if (!isLoading) {
			if (partId) {
				if (partId === "new") {
					handleNewRoll();
				} else {
					const partNode = gridApi?.getRowNode(partId);

					if (partNode) {
						gridApi?.ensureNodeVisible(partNode, "middle");
						partNode.setSelected(true);
						setSelectedRowData(partNode.data);
						setShowModal("update");
					}
				}
			}
		}
	}, [isLoading, gridApi]);

	useEffect(() => {
		registerConfig({
			id: "universalRollContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2
				container
				justifyContent="flex-start"
				direction="column"
				p={2}
				spacing={1}
				sx={{ backgroundColor: pageBgColor, minHeight: masterDetailsMinHeight }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Universal Rolls
					</Typography>
				</Grid2>
				<Grid2 container size={{ md: 12 }} spacing={1} alignItems="center">
					<Grid2 size={{ xs: 12, md: 10 }}>
						<TextFieldSearchFilter
							value={filterValue}
							onChange={(val) => {
								setFilterValue(val);
								gridApi?.setGridOption("quickFilterText", val);
							}}
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 2 }}>
						<ExcelButton
							handleExportToExcel={handleExportToExcel}
							disabled={!rows || rows.length === 0}
						/>
					</Grid2>
				</Grid2>
				<Grid2 sx={{ width: "100%" }}>
					<Group
						orientation="horizontal"
						defaultLayout={getSavedPanelLayout()}
						onLayoutChange={handlePanelResize}
						groupRef={groupRef}
						style={{ height: "100%" }}
					>
						<Panel id="table" minSize={20}>
							{selectedRows.length > 0 && (
								<CatalogsActionsHeader
									selectedRows={selectedRows}
									isDeletingAll={selectedRows.length == rows?.length}
									handleClearSelection={handleClearSelection}
									handleDelete={() => {
										if (selectedRows.length > 1) {
											setShowModal("deleteMultiple");
											return;
										}
										const row = selectedRows[0];
										setSelectedRowData(row);
										if (row.IsInActiveCarrier) {
											setShowModal("activeCarrier");
										} else if (row.IsInActiveRollset) {
											setShowModal("confirmRollsetDeactivation");
										} else {
											setShowModal("delete");
										}
									}}
									isLoading={false}
								/>
							)}
							<Paper
								elevation={2}
								style={{ height: "100%", width: "100%" }}
								id="universal-roll-table"
								className="ag-theme-material"
							>
								<AgGridReact<UniversalRoll>
									loading={isLoading}
									rowData={rows || []}
									columnDefs={columnDefs}
									rowModelType="clientSide"
									defaultColDef={defaultColDef}
									rowHeight={34}
									headerHeight={34}
									animateRows
									getContextMenuItems={getContextMenuItems}
									onGridReady={(params) => setGridApi(params.api)}
									getRowId={(params) => String(params.data.PartId)}
									loadingOverlayComponent={CenteredLazyLoading}
									onRowSelected={onRowSelected}
									onRowClicked={onRowClicked}
									gridOptions={{
										theme: themeDXT,
										rowSelection: {
											mode: "multiRow",
											checkboxes: true,
											enableClickSelection: false,
											headerCheckbox: true,
										},
										statusBar: {
											statusPanels: [
												{
													statusPanel: "agTotalRowCountComponent",
													align: "right",
												},
												{
													statusPanel: (params: IStatusPanelParams) => (
														<CustomStatusPanel
															handleNew={handleNewRoll}
															id="new-universal-roll"
														/>
													),
													align: "left",
												},
											],
										},
									}}
								/>
							</Paper>
						</Panel>
						<CustomSeparator onDoubleClick={handleSeparatorDoubleClick} />
						<Panel id="details" minSize={20}>
							<Paper
								elevation={2}
								style={{ height: "100%", width: "100%", overflowY: "scroll" }}
							>
								<UniversalRollDetails
									roll={selectedRowData}
									onHide={onModalHide}
									modal={
										showModal === "deleteMultiple"
											? ""
											: (showModal as "create" | "update" | "delete" | "")
									}
								/>
							</Paper>
						</Panel>
					</Group>
				</Grid2>
			</Grid2>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error in Universal Rolls"
			/>
			<DeleteEntity
				show={showModal === "delete"}
				entityParams={{
					Id: selectedRowData?.PartId!,
					Name: selectedRowData?.PartName!,
					Entity: "universal roll",
					QueryKey: "universalRoll",
				}}
				onHide={(update: boolean) => {
					onModalHide({ shouldUpdate: update, close: true });
					setShowModal(update ? "" : "update");
				}}
				deleteFunction={deleteUniversalRoll}
			/>
			<MultipleDeleteSelectModal
				open={showModal === "deleteMultiple"}
				onClose={(deleted: boolean) => {
					if (deleted) {
						setSelectedRows([]);
					}
					setShowModal("");
				}}
				deleteFunction={deleteUniversalRoll}
				selectedRows={selectedRows}
				entity={{
					queryKey: "universalRoll",
					name: "Universal Roll",
				}}
			/>

			<ActiveCarrierModal
				show={showModal === "activeCarrier"}
				action="delete"
				entityName={selectedRowData?.PartName || ""}
				entityType="Universal Roll"
				actionDescription="This universal roll is in an active carrier and cannot be deleted."
				carrier={
					selectedRowData
						? {
								CarrierName: selectedRowData.CarrierName,
								CarrierStatusName: selectedRowData.CarrierStatusName,
							}
						: null
				}
				rollsetInfo={
					selectedRowData?.RollsetId
						? {
								RollsetId: selectedRowData.RollsetId,
								RollsetLetter: selectedRowData.RollsetLetter,
								RollsetSectionName: selectedRowData.RollsetSectionName,
								RollsetStandName: selectedRowData.RollsetStandName,
								RollsetTopRollName: selectedRowData.RollsetTopRollName,
								RollsetBottomRollName: selectedRowData.RollsetBottomRollName,
							}
						: null
				}
				onClose={() => {
					setShowModal("");
					setSelectedRowData(null);
					handleClearSelection();
				}}
			/>
			<RollsetDeactivationConfirmModal
				show={showModal === "confirmRollsetDeactivation"}
				entityName={selectedRowData?.PartName || ""}
				entityType="Roll"
				actionDescription="Deleting this roll"
				rollsetInfo={
					selectedRowData?.RollsetId
						? {
								RollsetId: selectedRowData.RollsetId,
								RollsetLetter: selectedRowData.RollsetLetter,
								RollsetSectionName: selectedRowData.RollsetSectionName,
								RollsetStandName: selectedRowData.RollsetStandName,
								RollsetTopRollName: selectedRowData.RollsetTopRollName,
								RollsetBottomRollName: selectedRowData.RollsetBottomRollName,
							}
						: null
				}
				onConfirm={() => {
					setShowModal("delete");
				}}
				onCancel={() => {
					setShowModal("");
					setSelectedRowData(null);
					handleClearSelection();
				}}
			/>
		</>
	);
};
